import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { COLLS, PB_RECORDS, LEADS, DEMO_HUB, NUDGE_ALERTS, MEETINGS, MEETING_AI_NOTES } from '../constants';
import { authApi, leadsApi, collateralsApi, playbooksApi, nudgesApi, tokenStore,
  type LoginResponse, type LeadData, type CollateralData, type PlaybookData, type NudgeData } from '../api/client';
import type { DemoHub, User, Screen, Meeting, MeetingRecap, BotStatus } from '../types';

const USE_API = import.meta.env.VITE_USE_API === 'true';
const BOT_SERVER = (window as any).BOT_SERVER_OVERRIDE || 'http://localhost:3001';

interface AppContextValue {
  currentUser: User | null;
  screen: Screen;
  go: (screen: Screen) => void;
  login: (user: User) => void;
  loginWithCredentials: (email: string, password: string) => Promise<void>;
  logout: () => void;
  // Data — unified type (local or API-sourced)
  colls: CollateralData[];
  setColls: React.Dispatch<React.SetStateAction<CollateralData[]>>;
  playbooks: PlaybookData[];
  setPlaybooks: React.Dispatch<React.SetStateAction<PlaybookData[]>>;
  leads: LeadData[];
  addLead: (lead: Partial<LeadData>) => Promise<void>;
  updateLead: (id: string, updates: Partial<LeadData>) => Promise<void>;
  deleteLead: (id: string) => Promise<void>;
  demoHub: DemoHub;
  setDemoHub: React.Dispatch<React.SetStateAction<DemoHub>>;
  nudges: NudgeData[];
  setNudges: React.Dispatch<React.SetStateAction<NudgeData[]>>;
  // State
  libraryTab: 'collaterals' | 'playbooks';
  setLibraryTab: (tab: 'collaterals' | 'playbooks') => void;
  activePbIdx: number;
  setActivePbIdx: (idx: number) => void;
  isLoading: boolean;
  // Toast
  toast: (msg: string, type?: 'success' | 'error' | 'info') => void;
  toastMsg: string;
  toastVisible: boolean;
  toastType: 'success' | 'error' | 'info';
  // Meetings & AI Notetaker Bot
  meetings: Meeting[];
  setMeetings: React.Dispatch<React.SetStateAction<Meeting[]>>;
  meetingAiNotes: Record<string, MeetingRecap>;
  setMeetingAiNotes: React.Dispatch<React.SetStateAction<Record<string, MeetingRecap>>>;
  botServerOnline: boolean;
  inviteBot: (meetingId: string, meetUrl: string, joinMode: 'now' | 'scheduled', slot?: string) => Promise<void>;
  cancelBot: (meetingId: string) => Promise<void>;
}

const AppContext = createContext<AppContextValue | null>(null);

// ── Local mock data adapters ──
const mockColls: CollateralData[] = COLLS.map(c => ({
  _id: String(c.id),
  id: undefined,
  name: c.name, type: c.type, size: c.size, pages: c.pages, tags: c.tags,
  sharedBy: c.sharedBy, sharedByIni: c.sharedByIni, sharedDate: c.sharedDate,
  recipients: c.recipients, pdata: c.pdata,
  url: c.url, demoLogin: c.demoLogin, demoNote: c.demoNote,
}));

const mockPlaybooks: PlaybookData[] = PB_RECORDS.map((pb, i) => ({
  ...pb, _id: String(i),
  landingPage: { ...pb.landingPage, heroStyle: pb.landingPage.heroStyle as 'gradient' | 'minimal' },
  security: { ...pb.security, expiry: pb.security.expiry ?? null },
})) as unknown as PlaybookData[];

const mockLeads: LeadData[] = LEADS.map(l => ({ ...l, _id: l.id }));
const mockNudges: NudgeData[] = NUDGE_ALERTS.map(n => ({ ...n, _id: String(n.id) }));

const MOCK_USER_KEY = 'salesbook_mock_user';

function loadStoredUser(): User | null {
  if (USE_API) return null;
  try { return JSON.parse(localStorage.getItem(MOCK_USER_KEY) || 'null'); } catch { return null; }
}

export function AppProvider({ children }: { children: React.ReactNode }) {
  const storedUser = loadStoredUser();
  const [currentUser, setCurrentUser] = useState<User | null>(storedUser);
  const [screen, setScreen] = useState<Screen>(storedUser ? 'home' : 'login');
  const [colls, setColls] = useState<CollateralData[]>(mockColls);
  const [playbooks, setPlaybooks] = useState<PlaybookData[]>(mockPlaybooks);
  const [leads, setLeads] = useState<LeadData[]>(mockLeads);
  const [demoHub, setDemoHub] = useState<DemoHub>(DEMO_HUB);
  const [nudges, setNudges] = useState<NudgeData[]>(mockNudges);
  const [libraryTab, setLibraryTab] = useState<'collaterals' | 'playbooks'>('collaterals');
  const [activePbIdx, setActivePbIdx] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');

  // Meetings & bot state
  const [meetings, setMeetings] = useState<Meeting[]>(MEETINGS);
  const [meetingAiNotes, setMeetingAiNotes] = useState<Record<string, MeetingRecap>>(MEETING_AI_NOTES);
  const [botServerOnline, setBotServerOnline] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const toast = useCallback((msg: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToastMsg(msg); setToastType(type); setToastVisible(true);
    setTimeout(() => setToastVisible(false), 2800);
  }, []);

  const go = useCallback((s: Screen) => setScreen(s), []);

  // ── Bot WebSocket connection ─────────────────────────────────
  const handleBotEvent = useCallback((event: string, data: Record<string, unknown>) => {
    const meetingId = data.meetingId as string;
    if (!meetingId) return;

    const STATUS_MAP: Record<string, BotStatus> = {
      'bot.joining':     'joining',
      'bot.joined':      'recording',
      'bot.ended':       'transcribing',
      'bot.transcribing':'transcribing',
      'bot.summarising': 'summarising',
      'bot.recap':       'done',
      'bot.error':       'error',
    };

    if (STATUS_MAP[event]) {
      setMeetings(prev => prev.map(m =>
        m.id === meetingId ? { ...m, botStatus: STATUS_MAP[event] } : m
      ));
    }

    if (event === 'bot.recap' && data.recap) {
      const recap = data.recap as MeetingRecap;
      setMeetingAiNotes(prev => ({ ...prev, [meetingId]: recap }));
      setMeetings(prev => prev.map(m =>
        m.id === meetingId ? { ...m, hasRecap: true, botStatus: 'done', duration: recap.duration } : m
      ));
      toast('AI Recap ready — meeting notes generated', 'success');
    }

    if (event === 'bot.error') {
      toast(`Bot error: ${data.error || 'Unknown error'}`, 'error');
    }
  }, [toast]);

  const connectBotWS = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const wsUrl = BOT_SERVER.replace(/^http/, 'ws') + '/ws';
    let ws: WebSocket;
    try {
      ws = new WebSocket(wsUrl);
    } catch {
      return;
    }

    ws.onopen = () => {
      setBotServerOnline(true);
      wsRef.current = ws;
    };

    ws.onmessage = (e) => {
      try {
        const { event, data } = JSON.parse(e.data);
        if (event === 'connected') setBotServerOnline(true);
        else handleBotEvent(event, data || {});
      } catch {}
    };

    ws.onerror = () => {};

    ws.onclose = () => {
      setBotServerOnline(false);
      wsRef.current = null;
      // Reconnect after 10s
      reconnectTimer.current = setTimeout(connectBotWS, 10000);
    };
  }, [handleBotEvent]);

  // Check bot server health and connect WS on mount
  useEffect(() => {
    fetch(`${BOT_SERVER}/health`, { signal: AbortSignal.timeout(3000) })
      .then(() => connectBotWS())
      .catch(() => {});

    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [connectBotWS]);

  // ── Bot API actions ──────────────────────────────────────────
  const inviteBot = useCallback(async (
    meetingId: string,
    meetUrl: string,
    joinMode: 'now' | 'scheduled',
    slot?: string,
  ) => {
    const meeting = meetings.find(m => m.id === meetingId);
    if (!meeting) return;

    const payload = {
      meetUrl,
      meetingId,
      title: meeting.title,
      date: meeting.date,
      slot,
      participants: meeting.participants.map(p => `${p.name}${p.org ? ` (${p.org})` : ''}`),
    };

    const endpoint = joinMode === 'now' ? '/api/bot/join' : '/api/bot/schedule';
    const res = await fetch(`${BOT_SERVER}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(err.error || 'Bot server error');
    }

    setMeetings(prev => prev.map(m =>
      m.id === meetingId
        ? { ...m, botStatus: joinMode === 'now' ? 'joining' : 'scheduled' }
        : m
    ));
  }, [meetings]);

  const cancelBot = useCallback(async (meetingId: string) => {
    await fetch(`${BOT_SERVER}/api/bot/cancel/${meetingId}`, { method: 'POST' }).catch(() => {});
    setMeetings(prev => prev.map(m =>
      m.id === meetingId ? { ...m, botStatus: 'idle' } : m
    ));
  }, []);

  // ── Load data from API ──────────────────────────────────────
  const loadApiData = useCallback(async () => {
    if (!USE_API) return;
    try {
      setIsLoading(true);
      const [collRes, leadRes, pbRes, nudgeRes] = await Promise.allSettled([
        collateralsApi.list({ limit: '100' }),
        leadsApi.list({ limit: '100' }),
        playbooksApi.list({ limit: '100' }),
        nudgesApi.list(),
      ]);
      if (collRes.status === 'fulfilled' && collRes.value.data) setColls(collRes.value.data);
      if (leadRes.status === 'fulfilled' && leadRes.value.data) setLeads(leadRes.value.data);
      if (pbRes.status   === 'fulfilled' && pbRes.value.data)   setPlaybooks(pbRes.value.data);
      if (nudgeRes.status === 'fulfilled')                       setNudges(nudgeRes.value as NudgeData[]);
    } catch {
      toast('Using offline data — backend not connected', 'info');
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    const token = tokenStore.get();
    if (token && USE_API) {
      authApi.me().then(user => {
        setCurrentUser({ name: user.name, email: user.email, role: user.role as User['role'], title: user.title, ini: user.ini, color: user.color, pwd: '' });
        setScreen('home');
        loadApiData();
      }).catch(() => tokenStore.remove());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = () => { setCurrentUser(null); setScreen('login'); };
    window.addEventListener('auth:logout', handler);
    return () => window.removeEventListener('auth:logout', handler);
  }, []);

  const loginWithCredentials = useCallback(async (email: string, password: string) => {
    if (USE_API) {
      const res: LoginResponse = await authApi.login(email, password);
      tokenStore.set(res.token);
      const u = res.user;
      setCurrentUser({ name: u.name, email: u.email, role: u.role as User['role'], title: u.title, ini: u.ini, color: u.color, pwd: '' });
      setScreen('home');
      await loadApiData();
    } else {
      throw new Error('USE_API is false — use mock login');
    }
  }, [loadApiData]);

  const login = useCallback((user: User) => {
    if (!USE_API) localStorage.setItem(MOCK_USER_KEY, JSON.stringify(user));
    setCurrentUser(user);
    setScreen('home');
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(MOCK_USER_KEY);
    tokenStore.remove();
    setCurrentUser(null);
    setScreen('login');
    setColls(mockColls);
    setLeads(mockLeads);
    setPlaybooks(mockPlaybooks);
    setNudges(mockNudges);
  }, []);

  // ── Lead mutations ────────────────────────────────────────────
  const addLead = useCallback(async (lead: Partial<LeadData>) => {
    if (USE_API) {
      const created = await leadsApi.create(lead);
      setLeads(prev => [created, ...prev]);
    } else {
      const local: LeadData = { _id: `local-${Date.now()}`, name: '', ini: '', org: '', email: '', phone: '', heat: 'cold', intent: 0, lastSeen: 'Just now', firstSeen: 'Today', sourceType: 'manual', source: 'Added manually', signals: [], sessions: 0, totalTime: '—', status: 'new', ...lead };
      setLeads(prev => [local, ...prev]);
    }
  }, []);

  const updateLead = useCallback(async (id: string, updates: Partial<LeadData>) => {
    if (USE_API) {
      const updated = await leadsApi.update(id, updates);
      setLeads(prev => prev.map(l => l._id === id ? updated : l));
    } else {
      setLeads(prev => prev.map(l => l._id === id ? { ...l, ...updates } : l));
    }
  }, []);

  const deleteLead = useCallback(async (id: string) => {
    if (USE_API) await leadsApi.delete(id);
    setLeads(prev => prev.filter(l => l._id !== id));
  }, []);

  return (
    <AppContext.Provider value={{
      currentUser, screen, go, login, loginWithCredentials, logout,
      colls, setColls, playbooks, setPlaybooks,
      leads, addLead, updateLead, deleteLead,
      demoHub, setDemoHub, nudges, setNudges,
      libraryTab, setLibraryTab,
      activePbIdx, setActivePbIdx, isLoading,
      toast, toastMsg, toastVisible, toastType,
      meetings, setMeetings,
      meetingAiNotes, setMeetingAiNotes,
      botServerOnline,
      inviteBot,
      cancelBot,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
